import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';
import './chat-styles.css';
import './auth-styles.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  // Authentication
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authView, setAuthView] = useState('login'); // login or register
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ 
    email: '', 
    password: '', 
    username: '', 
    full_name: '', 
    role: 'arzt' 
  });
  
  const [currentView, setCurrentView] = useState('dashboard');
  const [patients, setPatients] = useState([]);
  const [studies, setStudies] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [segmentations, setSegmentations] = useState([]);
  const [stentPlans, setStentPlans] = useState([]);
  const [vesselAnalysis, setVesselAnalysis] = useState(null);
  
  // Patient form
  const [patientForm, setPatientForm] = useState({ name: '', patient_id: '', date_of_birth: '', gender: '' });
  
  // Study upload
  const [studyForm, setStudyForm] = useState({ study_type: 'CT', study_date: '', description: '' });
  const [uploadedImage, setUploadedImage] = useState(null);
  
  // Measurement tools
  const [activeTool, setActiveTool] = useState(null);
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  
  // Stent planning form
  const [stentForm, setStentForm] = useState({
    proximal_diameter: '',
    distal_diameter: '',
    length: '',
    landing_zone_proximal: 20,
    landing_zone_distal: 20,
    curvature_angle: '',
    notes: ''
  });
  
  // Image viewing
  const [zoom, setZoom] = useState(1);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [showSegmentation, setShowSegmentation] = useState(false);
  const [showCenterline, setShowCenterline] = useState(false);
  const [mprView, setMprView] = useState('axial'); // axial, sagittal, coronal, 3d
  const [darkMode, setDarkMode] = useState(false);
  
  // Panning
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [panStartX, setPanStartX] = useState(0);
  const [panStartY, setPanStartY] = useState(0);
  
  // Segmentation settings
  const [segAlgorithm, setSegAlgorithm] = useState('threshold');
  const [segThreshold, setSegThreshold] = useState(128);
  
  // Processing
  const [isProcessing, setIsProcessing] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const chatContainerRef = useRef(null);

  useEffect(() => {
    // Check for stored token
    const token = localStorage.getItem('angio_token');
    if (token) {
      verifyToken(token);
    }
    
    loadPatients();
    loadChatMessages();
    // Poll chat messages every 5 seconds
    const chatInterval = setInterval(loadChatMessages, 5000);
    return () => clearInterval(chatInterval);
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/verify?token=${token}`);
      if (response.data.valid) {
        setIsAuthenticated(true);
        // Get user details
        const userResponse = await axios.get(`${API}/users`);
        const user = userResponse.data.find(u => u.id === response.data.user_id);
        if (user) {
          setCurrentUser(user);
        }
      }
    } catch (error) {
      localStorage.removeItem('angio_token');
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem('angio_token', response.data.token);
      setCurrentUser(response.data.user);
      setIsAuthenticated(true);
      setLoginForm({ email: '', password: '' });
      alert(response.data.message);
    } catch (error) {
      console.error('Login error:', error);
      alert(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/register`, registerForm);
      localStorage.setItem('angio_token', response.data.token);
      setCurrentUser(response.data.user);
      setIsAuthenticated(true);
      setRegisterForm({ email: '', password: '', username: '', full_name: '', role: 'arzt' });
      alert(response.data.message);
    } catch (error) {
      console.error('Register error:', error);
      alert(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('angio_token');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setCurrentView('dashboard');
    alert('Erfolgreich abgemeldet');
  };

  // Mouse wheel zoom handler
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.max(0.5, Math.min(5, zoom + delta));
    setZoom(newZoom);
  };

  // Pan handlers
  const handleMouseDown = (e) => {
    if (activeTool === 'pan') {
      setIsPanning(true);
      setPanStartX(e.clientX - panX);
      setPanStartY(e.clientY - panY);
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning && activeTool === 'pan') {
      setPanX(e.clientX - panStartX);
      setPanY(e.clientY - panStartY);
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const loadPatients = async () => {
    try {
      const response = await axios.get(`${API}/patients`);
      setPatients(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Patienten:', error);
    }
  };

  const loadStudies = async (patientId) => {
    try {
      const response = await axios.get(`${API}/studies?patient_id=${patientId}`);
      setStudies(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Studien:', error);
    }
  };

  const loadMeasurements = async (studyId) => {
    try {
      const response = await axios.get(`${API}/measurements?study_id=${studyId}`);
      setMeasurements(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Messungen:', error);
    }
  };

  const loadSegmentations = async (studyId) => {
    try {
      const response = await axios.get(`${API}/segmentation/${studyId}`);
      setSegmentations(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Segmentierungen:', error);
    }
  };

  const loadStentPlans = async (studyId) => {
    try {
      const response = await axios.get(`${API}/stent-planning/${studyId}`);
      setStentPlans(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Stent-Planungen:', error);
    }
  };

  const loadVesselAnalysis = async (studyId) => {
    try {
      const response = await axios.get(`${API}/vessel-analysis/${studyId}`);
      if (response.data && response.data.length > 0) {
        setVesselAnalysis(response.data[0]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Gefäßanalyse:', error);
    }
  };

  const loadChatMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat`);
      setChatMessages(response.data);
      // Auto-scroll to bottom
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Fehler beim Laden der Chat-Nachrichten:', error);
    }
  };

  const sendChatMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    try {
      await axios.post(`${API}/chat`, {
        user_name: currentUser?.full_name || 'Dr. Medical',
        message: chatInput
      });
      setChatInput('');
      loadChatMessages();
    } catch (error) {
      console.error('Fehler beim Senden der Nachricht:', error);
      alert('Fehler beim Senden der Nachricht');
    }
  };

  const createPatient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/patients`, patientForm);
      setPatientForm({ name: '', patient_id: '', date_of_birth: '', gender: '' });
      loadPatients();
      alert('Patient erfolgreich erstellt!');
      setCurrentView('patients');
    } catch (error) {
      console.error('Fehler beim Erstellen des Patienten:', error);
      alert('Fehler beim Erstellen des Patienten');
    }
  };

  const uploadStudy = async (e) => {
    e.preventDefault();
    if (!uploadedImage || !selectedPatient) {
      alert('Bitte wählen Sie einen Patienten und laden Sie ein Bild hoch');
      return;
    }

    try {
      const studyData = {
        patient_id: selectedPatient.id,
        study_type: studyForm.study_type,
        study_date: studyForm.study_date || new Date().toISOString().split('T')[0],
        description: studyForm.description,
        image_data: uploadedImage,
        metadata: {}
      };

      await axios.post(`${API}/studies`, studyData);
      setStudyForm({ study_type: 'CT', study_date: '', description: '' });
      setUploadedImage(null);
      loadStudies(selectedPatient.id);
      alert('Studie erfolgreich hochgeladen!');
    } catch (error) {
      console.error('Fehler beim Hochladen der Studie:', error);
      alert('Fehler beim Hochladen der Studie');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCanvasClick = (e) => {
    if (!activeTool || !canvasRef.current || activeTool === 'pan') return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left - panX) / zoom;
    const y = (e.clientY - rect.top - panY) / zoom;

    const newPoint = { x, y };
    const newPoints = [...measurementPoints, newPoint];
    setMeasurementPoints(newPoints);

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#3ebaed';
    ctx.beginPath();
    ctx.arc(x * zoom + panX, y * zoom + panY, 5, 0, 2 * Math.PI);
    ctx.fill();

    if (activeTool === 'line' && newPoints.length === 2) {
      const distance = Math.sqrt(
        Math.pow(newPoints[1].x - newPoints[0].x, 2) +
        Math.pow(newPoints[1].y - newPoints[0].y, 2)
      );
      
      ctx.strokeStyle = '#3ebaed';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(newPoints[0].x * zoom + panX, newPoints[0].y * zoom + panY);
      ctx.lineTo(newPoints[1].x * zoom + panX, newPoints[1].y * zoom + panY);
      ctx.stroke();

      saveMeasurement('length', distance, 'px', newPoints);
      setMeasurementPoints([]);
      setActiveTool(null);
    }

    if (activeTool === 'diameter' && newPoints.length === 2) {
      const diameter = Math.sqrt(
        Math.pow(newPoints[1].x - newPoints[0].x, 2) +
        Math.pow(newPoints[1].y - newPoints[0].y, 2)
      );
      
      ctx.strokeStyle = '#3ebaed';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(newPoints[0].x * zoom + panX, newPoints[0].y * zoom + panY);
      ctx.lineTo(newPoints[1].x * zoom + panX, newPoints[1].y * zoom + panY);
      ctx.stroke();

      saveMeasurement('diameter', diameter, 'px', newPoints);
      setMeasurementPoints([]);
      setActiveTool(null);
    }

    if (activeTool === 'angle' && newPoints.length === 3) {
      const v1 = { x: newPoints[1].x - newPoints[0].x, y: newPoints[1].y - newPoints[0].y };
      const v2 = { x: newPoints[2].x - newPoints[1].x, y: newPoints[2].y - newPoints[1].y };
      
      const dot = v1.x * v2.x + v1.y * v2.y;
      const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
      const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
      const angle = Math.acos(dot / (mag1 * mag2)) * (180 / Math.PI);

      ctx.strokeStyle = '#3ebaed';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(newPoints[0].x * zoom + panX, newPoints[0].y * zoom + panY);
      ctx.lineTo(newPoints[1].x * zoom + panX, newPoints[1].y * zoom + panY);
      ctx.lineTo(newPoints[2].x * zoom + panX, newPoints[2].y * zoom + panY);
      ctx.stroke();

      saveMeasurement('angle', angle, '°', newPoints);
      setMeasurementPoints([]);
      setActiveTool(null);
    }

    if (activeTool === 'area') {
      ctx.strokeStyle = '#3ebaed';
      ctx.lineWidth = 2;
      
      if (newPoints.length > 1) {
        ctx.beginPath();
        ctx.moveTo(newPoints[0].x * zoom + panX, newPoints[0].y * zoom + panY);
        for (let i = 1; i < newPoints.length; i++) {
          ctx.lineTo(newPoints[i].x * zoom + panX, newPoints[i].y * zoom + panY);
        }
        ctx.stroke();
      }

      if (newPoints.length >= 4) {
        ctx.closePath();
        ctx.stroke();
        
        let area = 0;
        for (let i = 0; i < newPoints.length; i++) {
          const j = (i + 1) % newPoints.length;
          area += newPoints[i].x * newPoints[j].y;
          area -= newPoints[j].x * newPoints[i].y;
        }
        area = Math.abs(area / 2);

        saveMeasurement('area', area, 'px²', newPoints);
        setMeasurementPoints([]);
        setActiveTool(null);
      }
    }

    // Plaque marking
    if (activeTool === 'plaque') {
      saveMeasurement('plaque', 0, 'marker', [newPoint]);
      setMeasurementPoints([]);
    }

    // Aneurysm marking
    if (activeTool === 'aneurysm') {
      saveMeasurement('aneurysm', 0, 'marker', [newPoint]);
      setMeasurementPoints([]);
    }
  };

  const saveMeasurement = async (type, value, unit, coords) => {
    if (!selectedStudy) return;

    try {
      const measurementData = {
        study_id: selectedStudy.id,
        measurement_type: type,
        value: parseFloat(value.toFixed(2)),
        unit: unit,
        coordinates: coords,
        notes: ''
      };

      await axios.post(`${API}/measurements`, measurementData);
      loadMeasurements(selectedStudy.id);
    } catch (error) {
      console.error('Fehler beim Speichern der Messung:', error);
    }
  };

  const deleteMeasurement = async (measurementId) => {
    try {
      await axios.delete(`${API}/measurements/${measurementId}`);
      loadMeasurements(selectedStudy.id);
      redrawCanvas();
    } catch (error) {
      console.error('Fehler beim Löschen der Messung:', error);
    }
  };

  const performSegmentation = async () => {
    if (!selectedStudy) {
      alert('Bitte wählen Sie eine Studie aus');
      return;
    }

    setIsProcessing(true);
    try {
      const segmentationData = {
        study_id: selectedStudy.id,
        image_data: selectedStudy.image_data,
        threshold: segThreshold,
        vessel_type: 'artery',
        algorithm: segAlgorithm
      };

      await axios.post(`${API}/segmentation`, segmentationData);
      loadSegmentations(selectedStudy.id);
      setShowSegmentation(true);
      alert('Segmentierung erfolgreich durchgeführt!');
    } catch (error) {
      console.error('Fehler bei der Segmentierung:', error);
      alert('Fehler bei der Segmentierung');
    } finally {
      setIsProcessing(false);
    }
  };

  const performVesselAnalysis = async () => {
    if (!selectedStudy) {
      alert('Bitte wählen Sie eine Studie aus');
      return;
    }

    setIsProcessing(true);
    try {
      const response = await axios.post(`${API}/vessel-analysis/${selectedStudy.id}`);
      setVesselAnalysis(response.data);
      setShowCenterline(true);
      alert('Gefäßanalyse erfolgreich durchgeführt!');
    } catch (error) {
      console.error('Fehler bei der Gefäßanalyse:', error);
      alert('Fehler bei der Gefäßanalyse. Bitte führen Sie zuerst eine Segmentierung durch.');
    } finally {
      setIsProcessing(false);
    }
  };

  const enhanceImage = async (operation) => {
    if (!selectedStudy) return;

    setIsProcessing(true);
    try {
      const response = await axios.post(`${API}/image-processing`, {
        image_data: selectedStudy.image_data,
        operation: operation,
        parameters: {}
      });

      setSelectedStudy({ ...selectedStudy, image_data: response.data.processed_image });
    } catch (error) {
      console.error('Fehler bei der Bildverarbeitung:', error);
      alert('Fehler bei der Bildverarbeitung');
    } finally {
      setIsProcessing(false);
    }
  };

  const createStentPlan = async (e) => {
    e.preventDefault();
    if (!selectedStudy) {
      alert('Bitte wählen Sie eine Studie aus');
      return;
    }

    try {
      const planData = {
        study_id: selectedStudy.id,
        proximal_diameter: parseFloat(stentForm.proximal_diameter),
        distal_diameter: parseFloat(stentForm.distal_diameter),
        length: parseFloat(stentForm.length),
        landing_zone_proximal: parseFloat(stentForm.landing_zone_proximal),
        landing_zone_distal: parseFloat(stentForm.landing_zone_distal),
        curvature_angle: stentForm.curvature_angle ? parseFloat(stentForm.curvature_angle) : null,
        notes: stentForm.notes
      };

      await axios.post(`${API}/stent-planning`, planData);
      setStentForm({
        proximal_diameter: '',
        distal_diameter: '',
        length: '',
        landing_zone_proximal: 20,
        landing_zone_distal: 20,
        curvature_angle: '',
        notes: ''
      });
      loadStentPlans(selectedStudy.id);
      alert('Stent-Planung erfolgreich erstellt!');
    } catch (error) {
      console.error('Fehler bei der Stent-Planung:', error);
      alert('Fehler bei der Stent-Planung');
    }
  };

  const generateReport = async () => {
    if (!selectedStudy || !selectedPatient) {
      alert('Bitte wählen Sie einen Patienten und eine Studie aus');
      return;
    }

    try {
      const canvas = canvasRef.current;
      const imageData = canvas ? canvas.toDataURL('image/png') : selectedStudy.image_data;

      const reportData = {
        study_id: selectedStudy.id,
        patient_name: selectedPatient.name,
        patient_id: selectedPatient.patient_id,
        study_type: selectedStudy.study_type,
        study_date: selectedStudy.study_date,
        measurements: measurements.map(m => ({
          type: m.measurement_type,
          value: m.value,
          unit: m.unit
        })),
        stent_planning: stentPlans.length > 0 ? stentPlans[0] : null,
        images: [imageData],
        conclusion: 'Gefäßanalyse durchgeführt. ' + 
                   (vesselAnalysis ? `Gesamtlänge: ${vesselAnalysis.total_length.toFixed(2)}px, ` +
                   `Min. Durchmesser: ${vesselAnalysis.min_diameter.toFixed(2)}px, ` +
                   `Max. Durchmesser: ${vesselAnalysis.max_diameter.toFixed(2)}px` : ''),
        physician_name: 'Dr. Med.'
      };

      const response = await axios.post(`${API}/reports`, reportData);
      
      const pdfResponse = await axios.post(
        `${API}/reports/${response.data.id}/pdf`,
        {},
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bericht_${response.data.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      alert('Bericht erfolgreich generiert und heruntergeladen!');
    } catch (error) {
      console.error('Fehler beim Generieren des Berichts:', error);
      alert('Fehler beim Generieren des Berichts');
    }
  };

  const exportMeasurementsCSV = () => {
    if (measurements.length === 0) {
      alert('Keine Messungen zum Exportieren vorhanden');
      return;
    }

    const csv = [
      ['Typ', 'Wert', 'Einheit', 'Notizen'].join(','),
      ...measurements.map(m => [
        m.measurement_type,
        m.value,
        m.unit,
        m.notes || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `messungen_${selectedStudy.id}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    alert('CSV erfolgreich exportiert!');
  };

  const captureScreenshot = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const imageData = canvas.toDataURL('image/png');
    
    const link = document.createElement('a');
    link.href = imageData;
    link.setAttribute('download', `screenshot_${Date.now()}.png`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    alert('Screenshot gespeichert!');
  };

  const redrawCanvas = () => {
    if (!canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    canvas.width = img.naturalWidth * zoom;
    canvas.height = img.naturalHeight * zoom;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(panX, panY);
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    ctx.filter = 'none';

    // Draw measurements
    measurements.forEach(m => {
      if (m.coordinates && m.coordinates.length >= 2) {
        ctx.strokeStyle = '#3ebaed';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(m.coordinates[0].x * zoom, m.coordinates[0].y * zoom);
        
        for (let i = 1; i < m.coordinates.length; i++) {
          ctx.lineTo(m.coordinates[i].x * zoom, m.coordinates[i].y * zoom);
        }
        
        if (m.measurement_type === 'area') {
          ctx.closePath();
        }
        ctx.stroke();

        ctx.fillStyle = '#3ebaed';
        m.coordinates.forEach(coord => {
          ctx.beginPath();
          ctx.arc(coord.x * zoom, coord.y * zoom, 5, 0, 2 * Math.PI);
          ctx.fill();
        });

        const labelX = m.coordinates[0].x * zoom;
        const labelY = m.coordinates[0].y * zoom - 10;
        ctx.fillStyle = '#3ebaed';
        ctx.font = '14px Arial';
        ctx.fillText(`${m.value.toFixed(2)} ${m.unit}`, labelX, labelY);
        
        // Mark plaque/aneurysm
        if (m.measurement_type === 'plaque') {
          ctx.fillStyle = '#ff9800';
          ctx.beginPath();
          ctx.arc(m.coordinates[0].x * zoom, m.coordinates[0].y * zoom, 8, 0, 2 * Math.PI);
          ctx.fill();
          ctx.fillText('Plaque', labelX, labelY);
        }
        if (m.measurement_type === 'aneurysm') {
          ctx.fillStyle = '#f44336';
          ctx.beginPath();
          ctx.arc(m.coordinates[0].x * zoom, m.coordinates[0].y * zoom, 8, 0, 2 * Math.PI);
          ctx.fill();
          ctx.fillText('Aneurysma', labelX, labelY);
        }
      }
    });

    // Draw centerline
    if (showCenterline && vesselAnalysis && vesselAnalysis.centerline_points) {
      ctx.strokeStyle = '#ff6b6b';
      ctx.lineWidth = 2;
      ctx.beginPath();
      
      const points = vesselAnalysis.centerline_points;
      if (points.length > 0) {
        ctx.moveTo(points[0].x * zoom, points[0].y * zoom);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x * zoom, points[i].y * zoom);
        }
        ctx.stroke();
      }
    }

    ctx.restore();
  };

  useEffect(() => {
    if (selectedStudy && canvasRef.current) {
      redrawCanvas();
    }
  }, [zoom, brightness, contrast, measurements, selectedStudy, showCenterline, vesselAnalysis, panX, panY]);

  const selectPatient = (patient) => {
    setSelectedPatient(patient);
    loadStudies(patient.id);
    setCurrentView('patient-detail');
  };

  const selectStudy = (study) => {
    setSelectedStudy(study);
    loadMeasurements(study.id);
    loadSegmentations(study.id);
    loadStentPlans(study.id);
    loadVesselAnalysis(study.id);
    setCurrentView('viewer');
  };

  const getPageTitle = () => {
    switch (currentView) {
      case 'dashboard': return 'Dashboard';
      case 'patients': return 'Patienten';
      case 'new-patient': return 'Neuer Patient';
      case 'patient-detail': return selectedPatient ? selectedPatient.name : 'Patient Detail';
      case 'viewer': return 'Viewer';
      case 'chat': return 'Chat';
      default: return 'Dashboard';
    }
  };

  // If not authenticated, show login/register
  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-logo">🩺</div>
            <h1>Angio Medical</h1>
            <p>Gefäßchirurgie Bildgebungssystem</p>
          </div>

          <div className="auth-tabs">
            <button 
              className={authView === 'login' ? 'auth-tab active' : 'auth-tab'}
              onClick={() => setAuthView('login')}
            >
              Anmelden
            </button>
            <button 
              className={authView === 'register' ? 'auth-tab active' : 'auth-tab'}
              onClick={() => setAuthView('register')}
            >
              Registrieren
            </button>
          </div>

          {authView === 'login' ? (
            <form onSubmit={handleLogin} className="auth-form">
              <div className="form-group">
                <label>E-Mail</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  required
                  className="form-input"
                  placeholder="ihre.email@beispiel.de"
                />
              </div>
              <div className="form-group">
                <label>Passwort</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  required
                  className="form-input"
                  placeholder="••••••••"
                />
              </div>
              <button type="submit" className="auth-submit-btn">
                Anmelden
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="auth-form">
              <div className="form-group">
                <label>Vollständiger Name</label>
                <input
                  type="text"
                  value={registerForm.full_name}
                  onChange={(e) => setRegisterForm({...registerForm, full_name: e.target.value})}
                  required
                  className="form-input"
                  placeholder="Dr. Max Mustermann"
                />
              </div>
              <div className="form-group">
                <label>Benutzername</label>
                <input
                  type="text"
                  value={registerForm.username}
                  onChange={(e) => setRegisterForm({...registerForm, username: e.target.value})}
                  required
                  className="form-input"
                  placeholder="dr.mustermann"
                />
              </div>
              <div className="form-group">
                <label>E-Mail</label>
                <input
                  type="email"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                  required
                  className="form-input"
                  placeholder="dr.mustermann@klinik.de"
                />
              </div>
              <div className="form-group">
                <label>Rolle</label>
                <select
                  value={registerForm.role}
                  onChange={(e) => setRegisterForm({...registerForm, role: e.target.value})}
                  className="form-input"
                >
                  <option value="arzt">Arzt</option>
                  <option value="assistent">Assistent</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
              <div className="form-group">
                <label>Passwort</label>
                <input
                  type="password"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                  required
                  className="form-input"
                  placeholder="••••••••"
                  minLength={6}
                />
              </div>
              <button type="submit" className="auth-submit-btn">
                Registrieren
              </button>
            </form>
          )}

          <div className="auth-footer">
            <p>© 2025 Angio Medical GmbH</p>
            <p>MDR 2017/745 konform</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`app-layout ${darkMode ? 'dark-mode' : ''}`}>
      {/* Main Sidebar */}
      <aside className={`main-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <div className="logo-icon">🩺</div>
            {!sidebarCollapsed && <span className="logo-text">Angio Medical</span>}
          </div>
          <button 
            className="collapse-btn" 
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Erweitern' : 'Einklappen'}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${currentView === 'dashboard' ? 'active' : ''}`}
            onClick={() => setCurrentView('dashboard')}
          >
            <span className="nav-icon">🏠</span>
            {!sidebarCollapsed && <span className="nav-label">Dashboard</span>}
          </button>

          <button 
            className={`nav-item ${currentView === 'patients' ? 'active' : ''}`}
            onClick={() => setCurrentView('patients')}
          >
            <span className="nav-icon">👥</span>
            {!sidebarCollapsed && <span className="nav-label">Patienten</span>}
          </button>

          <button 
            className={`nav-item ${currentView === 'chat' ? 'active' : ''}`}
            onClick={() => setCurrentView('chat')}
          >
            <span className="nav-icon">💬</span>
            {!sidebarCollapsed && <span className="nav-label">Chat</span>}
          </button>

          {selectedPatient && (
            <button 
              className={`nav-item ${currentView === 'patient-detail' ? 'active' : ''}`}
              onClick={() => setCurrentView('patient-detail')}
            >
              <span className="nav-icon">📋</span>
              {!sidebarCollapsed && <span className="nav-label">Patient Detail</span>}
            </button>
          )}

          {selectedStudy && (
            <button 
              className={`nav-item ${currentView === 'viewer' ? 'active' : ''}`}
              onClick={() => setCurrentView('viewer')}
            >
              <span className="nav-icon">🔬</span>
              {!sidebarCollapsed && <span className="nav-label">Viewer</span>}
            </button>
          )}

          {!sidebarCollapsed && <div className="nav-divider">FEATURES</div>}

          <button className="nav-item feature-item">
            <span className="nav-icon">📊</span>
            {!sidebarCollapsed && (
              <div className="nav-feature">
                <span className="nav-label">DICOM Import</span>
                <span className="nav-desc">CT, MRT, Angio</span>
              </div>
            )}
          </button>

          <button className="nav-item feature-item">
            <span className="nav-icon">📏</span>
            {!sidebarCollapsed && (
              <div className="nav-feature">
                <span className="nav-label">Messungen</span>
                <span className="nav-desc">Länge, Ø, Winkel, Fläche</span>
              </div>
            )}
          </button>

          <button className="nav-item feature-item">
            <span className="nav-icon">🔬</span>
            {!sidebarCollapsed && (
              <div className="nav-feature">
                <span className="nav-label">Segmentierung</span>
                <span className="nav-desc">4 Algorithmen</span>
              </div>
            )}
          </button>

          <button className="nav-item feature-item">
            <span className="nav-icon">🩺</span>
            {!sidebarCollapsed && (
              <div className="nav-feature">
                <span className="nav-label">Stent-Planung</span>
                <span className="nav-desc">Größenberechnung</span>
              </div>
            )}
          </button>

          <button className="nav-item feature-item">
            <span className="nav-icon">📈</span>
            {!sidebarCollapsed && (
              <div className="nav-feature">
                <span className="nav-label">Gefäßanalyse</span>
                <span className="nav-desc">Mittellinie & Krümmung</span>
              </div>
            )}
          </button>

          <button className="nav-item feature-item">
            <span className="nav-icon">📄</span>
            {!sidebarCollapsed && (
              <div className="nav-feature">
                <span className="nav-label">PDF Reports</span>
                <span className="nav-desc">Prof. Berichte</span>
              </div>
            )}
          </button>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{currentUser?.full_name?.charAt(0) || '👨‍⚕️'}</div>
            {!sidebarCollapsed && (
              <div className="user-details">
                <div className="user-name">{currentUser?.full_name || 'Dr. Medical'}</div>
                <div className="user-role">{currentUser?.role === 'arzt' ? 'Arzt' : currentUser?.role === 'admin' ? 'Administrator' : 'Assistent'}</div>
              </div>
            )}
          </div>
          {!sidebarCollapsed && (
            <button onClick={handleLogout} className="logout-btn" title="Abmelden">
              🚪 Abmelden
            </button>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="main-content-wrapper">
        {/* Top Header */}
        <header className="top-header">
          <h1>{getPageTitle()}</h1>
          <button onClick={() => setCurrentView('new-patient')} className="header-action-btn">
            ➕ Neuer Patient
          </button>
          {currentView === 'viewer' && (
            <div className="header-controls">
              <button 
                onClick={() => setDarkMode(!darkMode)} 
                className="icon-btn"
                title="Dark Mode (OP)"
              >
                {darkMode ? '☀️' : '🌙'}
              </button>
              <button onClick={captureScreenshot} className="icon-btn" title="Screenshot">
                📸
              </button>
              <button onClick={exportMeasurementsCSV} className="icon-btn" title="CSV Export">
                📊
              </button>
            </div>
          )}
        </header>

        <main className="main-content-area">
          {/* Dashboard */}
          {currentView === 'dashboard' && (
            <div className="dashboard-view">
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">👥</div>
                  <div className="stat-content">
                    <div className="stat-value">{patients.length}</div>
                    <div className="stat-label">Patienten</div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">📊</div>
                  <div className="stat-content">
                    <div className="stat-value">{studies.length}</div>
                    <div className="stat-label">Studien</div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">📏</div>
                  <div className="stat-content">
                    <div className="stat-value">{measurements.length}</div>
                    <div className="stat-label">Messungen</div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">🩺</div>
                  <div className="stat-content">
                    <div className="stat-value">{stentPlans.length}</div>
                    <div className="stat-label">Stent-Planungen</div>
                  </div>
                </div>
              </div>

              {patients.length > 0 && (
                <div className="recent-section">
                  <h2>Letzte Patienten</h2>
                  <div className="recent-patients">
                    {patients.slice(0, 5).map(patient => (
                      <div key={patient.id} className="recent-patient-card" onClick={() => selectPatient(patient)}>
                        <div className="patient-icon">👤</div>
                        <div className="patient-info">
                          <h4>{patient.name}</h4>
                          <p>ID: {patient.patient_id}</p>
                        </div>
                        <button className="view-btn">Öffnen →</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Patients List */}
          {currentView === 'patients' && (
            <div className="patients-view">
              <div className="patients-grid">
                {patients.map(patient => (
                  <div key={patient.id} className="patient-card" onClick={() => selectPatient(patient)}>
                    <div className="patient-icon">👤</div>
                    <h3>{patient.name}</h3>
                    <p>ID: {patient.patient_id}</p>
                    {patient.date_of_birth && <p>Geb.: {patient.date_of_birth}</p>}
                    {patient.gender && <p>Geschlecht: {patient.gender}</p>}
                  </div>
                ))}
              </div>
              {patients.length === 0 && (
                <p className="empty-message">Noch keine Patienten vorhanden. Erstellen Sie einen neuen Patienten.</p>
              )}
            </div>
          )}

          {/* New Patient */}
          {currentView === 'new-patient' && (
            <div className="form-view">
              <form onSubmit={createPatient} className="form-card">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={patientForm.name}
                    onChange={(e) => setPatientForm({...patientForm, name: e.target.value})}
                    required
                    className="form-input"
                    placeholder="Dr. Max Mustermann"
                  />
                </div>
                <div className="form-group">
                  <label>Patienten-ID *</label>
                  <input
                    type="text"
                    value={patientForm.patient_id}
                    onChange={(e) => setPatientForm({...patientForm, patient_id: e.target.value})}
                    required
                    className="form-input"
                    placeholder="P12345"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Geburtsdatum</label>
                    <input
                      type="date"
                      value={patientForm.date_of_birth}
                      onChange={(e) => setPatientForm({...patientForm, date_of_birth: e.target.value})}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Geschlecht</label>
                    <select
                      value={patientForm.gender}
                      onChange={(e) => setPatientForm({...patientForm, gender: e.target.value})}
                      className="form-input"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="männlich">Männlich</option>
                      <option value="weiblich">Weiblich</option>
                      <option value="divers">Divers</option>
                    </select>
                  </div>
                </div>
                <button type="submit" className="submit-button">Patient erstellen</button>
              </form>
            </div>
          )}

          {/* Patient Detail */}
          {currentView === 'patient-detail' && selectedPatient && (
            <div className="patient-detail-view">
              <div className="detail-section">
                <h3>Neue Studie hochladen</h3>
                <form onSubmit={uploadStudy} className="form-card">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Studien-Typ</label>
                      <select
                        value={studyForm.study_type}
                        onChange={(e) => setStudyForm({...studyForm, study_type: e.target.value})}
                        className="form-input"
                      >
                        <option value="CT">CT</option>
                        <option value="MRT">MRT</option>
                        <option value="Angio">Angiographie</option>
                        <option value="DSA">DSA</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Datum</label>
                      <input
                        type="date"
                        value={studyForm.study_date}
                        onChange={(e) => setStudyForm({...studyForm, study_date: e.target.value})}
                        className="form-input"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Beschreibung</label>
                    <input
                      type="text"
                      value={studyForm.description}
                      onChange={(e) => setStudyForm({...studyForm, description: e.target.value})}
                      className="form-input"
                      placeholder="z.B. Abdominale Aorta"
                    />
                  </div>
                  <div className="form-group">
                    <label>Bild hochladen (DICOM/PNG/JPG)</label>
                    <input
                      type="file"
                      accept="image/*,.dcm"
                      onChange={handleImageUpload}
                      className="form-input"
                    />
                  </div>
                  {uploadedImage && (
                    <div className="image-preview">
                      <img src={uploadedImage} alt="Preview" />
                    </div>
                  )}
                  <button type="submit" className="submit-button">Studie hochladen</button>
                </form>
              </div>

              <div className="detail-section">
                <h3>Studien ({studies.length})</h3>
                <div className="studies-grid">
                  {studies.map(study => (
                    <div key={study.id} className="study-card" onClick={() => selectStudy(study)}>
                      <img src={study.image_data} alt="Study" className="study-thumbnail" />
                      <div className="study-info">
                        <h4>{study.study_type}</h4>
                        <p>{study.study_date}</p>
                        <p>{study.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {studies.length === 0 && (
                  <p className="empty-message">Noch keine Studien vorhanden.</p>
                )}
              </div>
            </div>
          )}

          {/* Chat */}
          {currentView === 'chat' && (
            <div className="chat-view">
              <div className="chat-header">
                <h2>💬 Team-Kommunikation</h2>
                <div className="online-badge">
                  <div className="online-dot"></div>
                  Online
                </div>
              </div>
              <div className="chat-container" ref={chatContainerRef}>
                {chatMessages.length === 0 ? (
                  <div className="chat-empty-state">
                    <div className="empty-icon">💬</div>
                    <p>Noch keine Nachrichten</p>
                    <p style={{fontSize: '0.9rem', opacity: 0.7}}>Starten Sie eine Unterhaltung mit Ihrem Team</p>
                  </div>
                ) : (
                  chatMessages.map((msg, idx) => (
                    <div key={idx} className={`chat-message ${msg.user_name === 'Dr. Medical' ? 'user-message' : 'other-message'}`}>
                      <div className="message-avatar">
                        {msg.user_name === 'Dr. Medical' ? '👨‍⚕️' : '👩‍⚕️'}
                      </div>
                      <div className="message-bubble">
                        <div className="message-header">
                          <span className="message-sender">{msg.user_name}</span>
                          <span className="message-time">{new Date(msg.timestamp).toLocaleString('de-DE', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}</span>
                        </div>
                        <div className="message-content">{msg.message}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <form onSubmit={sendChatMessage} className="chat-input-form">
                <div className="chat-input-wrapper">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Nachricht eingeben..."
                    className="chat-input"
                    autoComplete="off"
                  />
                </div>
                <button type="submit" className="chat-send-btn" disabled={!chatInput.trim()}>
                  <span>Senden</span>
                  <span>📤</span>
                </button>
              </form>
            </div>
          )}

          {/* Viewer */}
          {currentView === 'viewer' && selectedStudy && (
            <div className="viewer-layout">
              <div className="viewer-sidebar">
                <div className="sidebar-section">
                  <h3>Ansicht</h3>
                  <div className="view-mode-grid">
                    <button 
                      className={mprView === 'axial' ? 'view-btn active' : 'view-btn'}
                      onClick={() => setMprView('axial')}
                    >
                      📐 Axial
                    </button>
                    <button 
                      className={mprView === 'sagittal' ? 'view-btn active' : 'view-btn'}
                      onClick={() => setMprView('sagittal')}
                    >
                      📐 Sagittal
                    </button>
                    <button 
                      className={mprView === 'coronal' ? 'view-btn active' : 'view-btn'}
                      onClick={() => setMprView('coronal')}
                    >
                      📐 Coronal
                    </button>
                    <button 
                      className={mprView === '3d' ? 'view-btn active' : 'view-btn'}
                      onClick={() => setMprView('3d')}
                    >
                      🎲 3D
                    </button>
                  </div>
                </div>

                <div className="sidebar-section">
                  <h3>Bildeinstellungen</h3>
                  <div className="zoom-info">
                    <span>🔍 Zoom: {zoom.toFixed(1)}x</span>
                    <button onClick={() => setZoom(1)} className="reset-btn">Reset</button>
                  </div>
                  <p className="help-text">Mausrad zum Zoomen</p>
                  <div className="slider-group">
                    <label>Helligkeit: {brightness}%</label>
                    <input
                      type="range"
                      min="50"
                      max="150"
                      value={brightness}
                      onChange={(e) => setBrightness(parseInt(e.target.value))}
                      className="slider"
                    />
                  </div>
                  <div className="slider-group">
                    <label>Kontrast: {contrast}%</label>
                    <input
                      type="range"
                      min="50"
                      max="150"
                      value={contrast}
                      onChange={(e) => setContrast(parseInt(e.target.value))}
                      className="slider"
                    />
                  </div>
                </div>

                <div className="sidebar-section">
                  <h3>Navigation</h3>
                  <button
                    className={activeTool === 'pan' ? 'tool-btn active' : 'tool-btn'}
                    onClick={() => {
                      setActiveTool('pan');
                      setMeasurementPoints([]);
                    }}
                  >
                    🖐️ Pan/Verschieben
                  </button>
                  <button
                    onClick={() => { setPanX(0); setPanY(0); setZoom(1); }}
                    className="tool-btn"
                  >
                    🔄 Zurücksetzen
                  </button>
                </div>

                <div className="sidebar-section">
                  <h3>Bildverbesserung</h3>
                  <div className="tool-grid">
                    <button onClick={() => enhanceImage('enhance')} className="tool-btn" disabled={isProcessing}>
                      ✨ Verbessern
                    </button>
                    <button onClick={() => enhanceImage('denoise')} className="tool-btn" disabled={isProcessing}>
                      🧹 Entrauschen
                    </button>
                    <button onClick={() => enhanceImage('sharpen')} className="tool-btn" disabled={isProcessing}>
                      🔪 Schärfen
                    </button>
                    <button onClick={() => enhanceImage('edge_detect')} className="tool-btn" disabled={isProcessing}>
                      🔍 Kanten
                    </button>
                  </div>
                </div>

                <div className="sidebar-section">
                  <h3>Messwerkzeuge</h3>
                  <div className="tool-grid">
                    <button
                      className={activeTool === 'line' ? 'tool-btn active' : 'tool-btn'}
                      onClick={() => {
                        setActiveTool('line');
                        setMeasurementPoints([]);
                      }}
                    >
                      📏 Länge
                    </button>
                    <button
                      className={activeTool === 'diameter' ? 'tool-btn active' : 'tool-btn'}
                      onClick={() => {
                        setActiveTool('diameter');
                        setMeasurementPoints([]);
                      }}
                    >
                      ⭕ Durchmesser
                    </button>
                    <button
                      className={activeTool === 'angle' ? 'tool-btn active' : 'tool-btn'}
                      onClick={() => {
                        setActiveTool('angle');
                        setMeasurementPoints([]);
                      }}
                    >
                      📐 Winkel
                    </button>
                    <button
                      className={activeTool === 'area' ? 'tool-btn active' : 'tool-btn'}
                      onClick={() => {
                        setActiveTool('area');
                        setMeasurementPoints([]);
                      }}
                    >
                      ⬜ Fläche
                    </button>
                  </div>
                  {activeTool && activeTool !== 'pan' && (
                    <div className="tool-hint">
                      {activeTool === 'line' && '📍 Klicken Sie 2 Punkte'}
                      {activeTool === 'diameter' && '📍 Klicken Sie 2 Punkte'}
                      {activeTool === 'angle' && '📍 Klicken Sie 3 Punkte'}
                      {activeTool === 'area' && '📍 Klicken Sie 4+ Punkte für Polygon'}
                    </div>
                  )}
                </div>

                <div className="sidebar-section">
                  <h3>Pathologie-Markierung</h3>
                  <div className="tool-grid">
                    <button
                      className={activeTool === 'plaque' ? 'tool-btn active' : 'tool-btn'}
                      onClick={() => {
                        setActiveTool('plaque');
                        setMeasurementPoints([]);
                      }}
                    >
                      🟠 Plaque
                    </button>
                    <button
                      className={activeTool === 'aneurysm' ? 'tool-btn active' : 'tool-btn'}
                      onClick={() => {
                        setActiveTool('aneurysm');
                        setMeasurementPoints([]);
                      }}
                    >
                      🔴 Aneurysma
                    </button>
                  </div>
                </div>

                <div className="sidebar-section">
                  <h3>Segmentierung</h3>
                  <div className="form-group-compact">
                    <label>Algorithmus</label>
                    <select
                      value={segAlgorithm}
                      onChange={(e) => setSegAlgorithm(e.target.value)}
                      className="form-input-compact"
                    >
                      <option value="threshold">Schwellwert</option>
                      <option value="adaptive">Adaptiv</option>
                      <option value="canny">Canny Edge</option>
                      <option value="watershed">Watershed</option>
                    </select>
                  </div>
                  <div className="slider-group">
                    <label>Schwellwert: {segThreshold}</label>
                    <input
                      type="range"
                      min="50"
                      max="200"
                      value={segThreshold}
                      onChange={(e) => setSegThreshold(parseInt(e.target.value))}
                      className="slider"
                    />
                  </div>
                  <button onClick={performSegmentation} className="action-button" disabled={isProcessing}>
                    {isProcessing ? '⏳ Verarbeite...' : '🔬 Segmentierung'}
                  </button>
                  {segmentations.length > 0 && (
                    <div className="toggle-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={showSegmentation}
                          onChange={(e) => setShowSegmentation(e.target.checked)}
                        />
                        Segmentierung anzeigen
                      </label>
                    </div>
                  )}
                </div>

                <div className="sidebar-section">
                  <h3>Gefäßanalyse</h3>
                  <button onClick={performVesselAnalysis} className="action-button" disabled={isProcessing}>
                    {isProcessing ? '⏳ Analysiere...' : '📈 Analyse starten'}
                  </button>
                  {vesselAnalysis && (
                    <div className="analysis-results">
                      <div className="result-item">
                        <span>Gesamtlänge:</span>
                        <strong>{vesselAnalysis.total_length.toFixed(2)} px</strong>
                      </div>
                      <div className="result-item">
                        <span>Min. Ø:</span>
                        <strong>{vesselAnalysis.min_diameter.toFixed(2)} px</strong>
                      </div>
                      <div className="result-item">
                        <span>Max. Ø:</span>
                        <strong>{vesselAnalysis.max_diameter.toFixed(2)} px</strong>
                      </div>
                      <div className="result-item">
                        <span>Max. Krümmung:</span>
                        <strong>{vesselAnalysis.max_curvature.toFixed(2)}°</strong>
                      </div>
                      <div className="toggle-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={showCenterline}
                            onChange={(e) => setShowCenterline(e.target.checked)}
                          />
                          Mittellinie anzeigen
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="sidebar-section">
                  <h3>Messungen ({measurements.length})</h3>
                  <div className="measurements-list">
                    {measurements.map(m => (
                      <div key={m.id} className="measurement-item">
                        <span>
                          {m.measurement_type === 'length' && '📏'}
                          {m.measurement_type === 'diameter' && '⭕'}
                          {m.measurement_type === 'angle' && '📐'}
                          {m.measurement_type === 'area' && '⬜'}
                          {m.measurement_type === 'plaque' && '🟠'}
                          {m.measurement_type === 'aneurysm' && '🔴'}
                          {' '}{m.measurement_type}: {m.value.toFixed(2)} {m.unit}
                        </span>
                        <button onClick={() => deleteMeasurement(m.id)} className="delete-btn">×</button>
                      </div>
                    ))}
                  </div>
                  {measurements.length === 0 && (
                    <p className="empty-message-small">Keine Messungen</p>
                  )}
                </div>

                <div className="sidebar-section">
                  <h3>Stent-Planung</h3>
                  <form onSubmit={createStentPlan} className="stent-form">
                    <div className="form-group-compact">
                      <label>Proximal Ø (mm)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={stentForm.proximal_diameter}
                        onChange={(e) => setStentForm({...stentForm, proximal_diameter: e.target.value})}
                        className="form-input-compact"
                        placeholder="z.B. 24.5"
                        required
                      />
                    </div>
                    <div className="form-group-compact">
                      <label>Distal Ø (mm)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={stentForm.distal_diameter}
                        onChange={(e) => setStentForm({...stentForm, distal_diameter: e.target.value})}
                        className="form-input-compact"
                        placeholder="z.B. 22.0"
                        required
                      />
                    </div>
                    <div className="form-group-compact">
                      <label>Läsionslänge (mm)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={stentForm.length}
                        onChange={(e) => setStentForm({...stentForm, length: e.target.value})}
                        className="form-input-compact"
                        placeholder="z.B. 120"
                        required
                      />
                    </div>
                    <button type="submit" className="action-button">Berechnen</button>
                  </form>

                  <div className="stent-plans-list">
                    {stentPlans.map(plan => (
                      <div key={plan.id} className="stent-plan-item">
                        <strong>🩺 Empfohlen: {plan.recommended_stent_size}</strong>
                        <p>Proximal: {plan.proximal_diameter} mm</p>
                        <p>Distal: {plan.distal_diameter} mm</p>
                        <p>Läsionslänge: {plan.length} mm</p>
                        <p>Oversizing: {plan.oversizing_percentage}%</p>
                        {plan.risk_assessment && (
                          <p className="risk-info">⚠️ {plan.risk_assessment}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="sidebar-section">
                  <h3>Export</h3>
                  <div className="export-grid">
                    <button onClick={generateReport} className="action-button">
                      📄 PDF
                    </button>
                    <button onClick={exportMeasurementsCSV} className="action-button">
                      📊 CSV
                    </button>
                    <button onClick={captureScreenshot} className="action-button">
                      📸 Screenshot
                    </button>
                  </div>
                </div>
              </div>

              <div className="viewer-main">
                <div className="mpr-badge">{mprView.toUpperCase()}</div>
                <div 
                  className="canvas-container"
                  onWheel={handleWheel}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  <img
                    ref={imageRef}
                    src={selectedStudy.image_data}
                    alt="Study"
                    style={{ display: 'none' }}
                    onLoad={redrawCanvas}
                  />
                  <canvas
                    ref={canvasRef}
                    onClick={handleCanvasClick}
                    style={{ 
                      cursor: activeTool === 'pan' ? 'grab' : activeTool ? 'crosshair' : 'default',
                      transform: `translate(${panX}px, ${panY}px)`
                    }}
                  />
                </div>
                
                {showSegmentation && segmentations.length > 0 && (
                  <div className="segmentation-overlay">
                    <h4>Segmentierung</h4>
                    <img src={segmentations[0].segmentation_data} alt="Segmentation" className="segmentation-image" />
                    {segmentations[0].centerline_data && (
                      <div>
                        <h4>Mittellinie</h4>
                        <img src={segmentations[0].centerline_data} alt="Centerline" className="segmentation-image" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {isProcessing && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Verarbeite...</p>
        </div>
      )}
    </div>
  );
}

export default App;